import { useCallback } from 'react';

import { randomUUID as uuidv4 } from 'expo-crypto';
import { useRouter } from 'expo-router';
import {
  logOfferAddedToOrder,
  logRewardAddedToOrder,
} from 'features/analytics/incentive-added-to-order';
import { IAddToCartSelectionAttributes } from 'features/cart/utils/analytics';
import {
  parseOfferListPosition,
  toAmplitudeOfferListPosition,
} from 'features/offer/utils/analytics';
import { useStoreContext } from 'features/store';
import { differenceBy, intersectionBy } from 'lodash-es';

import { ICombo, ILegacyCartEntry } from '@rbi-ctg/menu';
import {
  transformUserSelectionsComboToComboSlotSelections,
  transformUserSelectionsModifierToModifierSelection,
} from 'components/product-detail-v2/utils';
import { toaster } from 'components/toaster/toaster';
import { useDistance } from 'hooks/geolocation/use-distance';
import { useProductPrice } from 'hooks/menu/use-product-price';
import { useRoute } from 'hooks/navigation/use-route';
import { useCart } from 'hooks/use-cart';
import { useRedeemReward } from 'pages/cart/cart-item/hooks/use-redeem-reward-state';
import { mapEntryTypeToOfferType } from 'state/cart/helpers';
import { useFavoritesContext } from 'state/favorites';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import {
  getCartIdToEvictForNewRewardSlot,
  sumAppliedTimesApplied,
} from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useGetAvailableRewards } from 'state/loyalty/hooks/use-get-available-rewards';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';
import { LoyaltyAppliedOffer, isLoyaltyOffer } from 'state/loyalty/types';
import { findRewardInAppliedRewards } from 'state/loyalty/utils';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { useEditingCartEntry } from 'state/product-wizard-v2/hooks/use-editing-cart-entry';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { getCartEntriesBenefit } from 'utils/cart/helper';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';
import { routes } from 'utils/routing';
import { transformMenuObjectToCartItem } from 'utils/wizard';
import { IMenuObjectToCartEntry } from 'utils/wizard/types';

export const useProductCart = () => {
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const rewardRedemptionLimit = useRewardRedemptionLimit();
  const { editFavorite, tempFavorite: currentFavorite } = useFavoritesContext();
  const { getAvailableRewardFromCartEntry } = useGetAvailableRewards();
  const { isOffer } = useMenuContext();
  const {
    addItemToCart,
    cartEntries,
    removeFromCart,
    repriceCartEntries,
    updateCartEntry,
  } = useCart();
  const {
    selectedProduct,
    productQuantity,
    userSelections,
    manualModifications,
  } = useProductWizardContext();

  const dispatch = useAppDispatch();
  const editingCartEntry = useEditingCartEntry();
  const { totalPrice } = useProductPrice();
  const { incentiveNotInMenu } = useRedeemReward();

  const { pathname, params } = useRoute<{ olp?: string }>();
  const offerListPosition = parseOfferListPosition(params?.olp);
  const { rewardBenefitId, cartIdEditing } = useProductWizardFlow();
  const router = useRouter();

  const { store } = useStoreContext();
  const distanceToStore = useDistance({
    restaurant: store,
    shouldUseUserCoordinatesIfAvailable: true,
  });

  const shouldDisplayMenuV2Toast = !!pathname.startsWith(routes.menu);

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const appliedLoyaltyRewardsArray = useAppSelector(
    selectors.loyalty.selectAppliedLoyaltyRewardsArray
  );
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const selectedOffer = selectedLoyaltyOffer;

  // Adds multiple cart entries to the cart
  const addMultipleToCart = useCallback(
    (
      newCartEntries: ILegacyCartEntry[],
      selectionAttributes?: IAddToCartSelectionAttributes | undefined
    ) => {
      const showBulkMessage = newCartEntries.length > 1;

      newCartEntries.forEach(entry => {
        addItemToCart(entry, selectionAttributes, !showBulkMessage);
      });

      if (showBulkMessage) {
        if (shouldDisplayMenuV2Toast) {
          // Menu Screens Toast
          return toaster.addToCartToast(
            t('addToCartMultipleSuccess', {
              itemName: newCartEntries[0].name,
              quantity: newCartEntries.length - 1,
            }),
            newCartEntries[0].image
          );
        }

        toaster.positive(
          t('addToCartMultipleSuccess', {
            itemName: newCartEntries[0].name,
            quantity: newCartEntries.length - 1,
          })
        );
      }
    },
    [addItemToCart, shouldDisplayMenuV2Toast]
  );

  // Adds or Updates CartEntries
  // Determines which ones are being updated and which ones are new using their cartIds
  const upsertCart = useCallback(
    (
      upsertCartEntries: ILegacyCartEntry[],
      selectionAttributes?: IAddToCartSelectionAttributes | undefined
    ) => {
      const newEntries = differenceBy(upsertCartEntries, cartEntries, 'cartId');
      if (newEntries?.length) {
        addMultipleToCart(newEntries, selectionAttributes);
      }

      const existingEntries = intersectionBy(upsertCartEntries, cartEntries, 'cartId');
      // When cart V2 is enabled and the user increase the quantity of an item
      // we are creating a new entries with the same _id.
      // On that case we don't want to show the message when updating an existing same _id entries.
      const differentEntries = differenceBy(existingEntries, newEntries, '_id');
      const skipShowMessage = !differentEntries.length;

      existingEntries.forEach((newEntry: ILegacyCartEntry) =>
        updateCartEntry(newEntry, newEntry.cartId, skipShowMessage)
      );
    },
    [addMultipleToCart, cartEntries, updateCartEntry]
  );

  // Cart v2 let user to duplicate an item using the incrementor and apply a reward to the duplicate item. As this could happens in some places in the app not always we have the rewardBenefitId in the params.
  // So we need to check if the rewardBenefitId is available and apply the reward to the cart entry.
  // If cart V2 is disabled we will always use the rewardBenefitId from the params.
  const processRewardRedemption = useCallback(
    (cartEntry: ILegacyCartEntry): boolean => {
      const rewardInfo = getAvailableRewardFromCartEntry(cartEntry);

      if (!rewardBenefitId && !rewardInfo?.rewardBenefitId) {
        return false;
      }

      // If enableRewardUnstacking flag is enabled and cart entry is the exact same as
      // a previously added reward do not apply nor remove any reward
      if (
        appliedLoyaltyRewardsArray?.length &&
        enableRewardUnstacking &&
        rewardInfo &&
        appliedLoyaltyRewardsArray.some(
          applied => applied.rewardId === rewardInfo.id && applied.cartId === cartEntry.cartId
        )
      ) {
        return false;
      }

      const { appliedReward, limitPerOrderMet } = findRewardInAppliedRewards(
        appliedLoyaltyRewardsArray || [],
        rewardInfo?.id || '',
        rewardInfo?.limitPerOrder
      );

      if (appliedReward && limitPerOrderMet) {
        // The incoming reward is already at its per-order limit. Remove the existing slot
        // from both the cart and Redux so the new applyReward passes the limitPerOrder check
        // without clobbering an unrelated reward via the oldest-slot eviction.
        removeFromCart({ cartId: appliedReward.cartId });
        dispatch(
          actions.loyalty.removeAppliedReward({
            cartId: appliedReward.cartId,
            rewardBenefitId: appliedReward.rewardBenefitId,
          })
        );
      } else if (enableRewardUnstacking) {
        // At global capacity (adding a reward while a different one occupies the only slot), the
        // `applyReward` reducer evicts the oldest applied-reward slot from Redux
        // (`rewards.slice.ts`), but it does not touch the legacy cart. Mirror `addToLegacyCart`
        // and remove that evicted slot's cart entry too; otherwise the old reward lingers in the
        // cart as a full-price item. `getCartIdToEvictForNewRewardSlot` targets the same oldest
        // slot (`Object.keys(...)[0]`) the reducer evicts, so cart and Redux stay in sync.
        const evictCartId = getCartIdToEvictForNewRewardSlot(
          appliedLoyaltyRewards,
          cartEntry.cartId,
          rewardRedemptionLimit
        );
        if (evictCartId) {
          removeFromCart({ cartId: evictCartId });
        }
      }

      const rewardPayload = {
        rewardBenefitId: rewardBenefitId || rewardInfo?.rewardBenefitId || '',
        cartId: cartEntry.cartId,
        redemptionMethod: RewardRedemptionMethod.MOBILE,
        // If a loyalty incentive is not available as a regular menu entry,
        // the quantity must match rewards applied
        ...(incentiveNotInMenu && { quantity: 1 }),
        enableRewardUnstacking,
        rewardRedemptionLimit,
      };

      dispatch(actions.loyalty.applyReward(rewardPayload));
      return true;
    },
    [
      getAvailableRewardFromCartEntry,
      rewardBenefitId,
      appliedLoyaltyRewards,
      appliedLoyaltyRewardsArray,
      incentiveNotInMenu,
      dispatch,
      removeFromCart,
      enableRewardUnstacking,
      rewardRedemptionLimit,
    ]
  );

  const handleRewardsRedemption = useCallback(
    (cartEntries: ILegacyCartEntry[]): boolean => {
      if (!rewardBenefitId) {
        return false;
      }

      const entriesRedemption = getCartEntriesBenefit(
        cartEntries,
        getAvailableRewardFromCartEntry,
        rewardBenefitId
      );

      if (entriesRedemption) {
        // When unstacking is on, cap the number of reward applications to the slots actually
        // available at dispatch time. Without this guard every entry in a qty>1 add dispatches
        // applyReward with a stale (pre-dispatch) appliedLoyaltyRewards snapshot, so the Redux
        // reducer sees sum >= limit on the last dispatch and evicts an unrelated existing reward.
        //
        // At full capacity when adding a NEW reward (not editing), allow exactly one dispatch
        // through so `processRewardRedemption` can perform the user-confirmed swap (the PDP
        // modal explicitly tells the user "this will replace one of your existing rewards" —
        // and the pricing path reserves a slot to preview the discount). Without this, the
        // slice would strip the only entry, the eviction branches at lines ~201/212 would
        // never run, and the new reward would silently be added at full price. Mirrors the
        // OneMenu add flow in `src/features/menu/components/product-wizard/use-add-to-cart.ts`.
        const rawSliceCap = Math.max(
          0,
          rewardRedemptionLimit - sumAppliedTimesApplied(appliedLoyaltyRewards)
        );
        const sliceCap = !cartIdEditing && rawSliceCap === 0 ? 1 : rawSliceCap;
        const entriesToRedeem = enableRewardUnstacking
          ? entriesRedemption.slice(0, sliceCap)
          : entriesRedemption;
        return entriesToRedeem.reduce(
          (didApplyReward, entry) => processRewardRedemption(entry) || didApplyReward,
          false
        );
      }
      return false;
    },
    [
      getAvailableRewardFromCartEntry,
      processRewardRedemption,
      rewardBenefitId,
      cartIdEditing,
      enableRewardUnstacking,
      rewardRedemptionLimit,
      appliedLoyaltyRewards,
    ]
  );

  const handleOffers = useCallback(
    ({
      mainEntry,
      upsellEntries,
      modifiersInjected,
      cartEntries,
    }: IMenuObjectToCartEntry & { cartEntries: ILegacyCartEntry[] }) => {
      const offerPrice = totalPrice(modifiersInjected);
      if (selectedOffer && isLoyaltyOffer(selectedOffer)) {
        if (!selectedOffer.isStackable) {
          appliedOffers.forEach(offer => {
            if (!offer.isStackable && !!offer.cartId) {
              removeFromCart({ cartId: offer.cartId });
            }
          });
        }
        dispatch(
          actions.loyalty.applyOffer({
            id: selectedOffer.loyaltyEngineId,
            type: (selectedOffer as LoyaltyAppliedOffer).type,
            cartId: mainEntry?.cartId,
            isStackable: selectedOffer.isStackable,
            cmsId: selectedOffer._id,
          })
        );
        dispatch(actions.loyalty.setSelectedOffer(null));
        upsertCart([
          {
            ...mainEntry,
            price: offerPrice,
            // FIXME: VendorConfigs types between LoyaltyOffer and ILegacyCartEntry does not match
            // @ts-ignore
            offerVendorConfigs: selectedOffer.vendorConfigs,
            ...(mainEntry && { type: mapEntryTypeToOfferType(mainEntry.type) }),
          },
          ...upsellEntries,
        ]);
      }
    },
    [appliedOffers, dispatch, removeFromCart, selectedOffer, totalPrice, upsertCart]
  );

  const handleTransformMenuObjectToCartItem = useCallback(
    ({ isNewItem, quantity }: { isNewItem: boolean; quantity: number }) => {
      const { mainEntry, upsellEntries, modifiersInjected } = transformMenuObjectToCartItem(
        selectedProduct,
        {
          quantity,
          modifierSelections: transformUserSelectionsModifierToModifierSelection(userSelections),
          pickerSelections: userSelections.pickerAspects,
          comboSlotSelections: transformUserSelectionsComboToComboSlotSelections(
            userSelections.comboSlot,
            selectedProduct as ICombo
          ),
          pathname,
          existingCartId: editingCartEntry?.cartId,
          shouldLogEvents: !!isOffer,
        }
      );

      if (isOffer && selectedOffer) {
        handleOffers({
          mainEntry,
          upsellEntries,
          modifiersInjected,
          cartEntries,
        });
      }

      const newCartId = isNewItem ? uuidv4() : editingCartEntry?.cartId || mainEntry?.cartId;

      const cartEntriesWithoutPrice = isOffer
        ? upsellEntries
        : ([
            {
              ...mainEntry,
              cartId: newCartId,
              lineId: newCartId,
            },
            ...upsellEntries,
          ] as ILegacyCartEntry[]);

      return repriceCartEntries(cartEntriesWithoutPrice);
    },
    [
      cartEntries,
      editingCartEntry?.cartId,
      handleOffers,
      isOffer,
      pathname,
      repriceCartEntries,
      selectedOffer,
      selectedProduct,
      userSelections,
    ]
  );

  /**
   * Creates cart entries from the quantity of the product
   * @param quantity - The quantity of new items to create
   * @returns The new cart entries
   */
  const createCartEntriesFromItemQuantity = useCallback(
    (quantity: number) => {
      const cartEntries: ILegacyCartEntry[] = [];
      for (let i = 0; i < quantity; i++) {
        const repricedCartEntries = handleTransformMenuObjectToCartItem({
          isNewItem: true,
          quantity: 1,
        });
        cartEntries.push(...repricedCartEntries);
      }
      return cartEntries;
    },
    [handleTransformMenuObjectToCartItem]
  );

  const addToOrder = useCallback(() => {
    let expandedCartEntries: ILegacyCartEntry[] = [];

    // If the product quantity is greater than 1, we need to create new cart entries for the same product.
    if (productQuantity > 1) {
      // We don't need to create a new cart entry for the first item because it's already in the cart.
      const quantityToCreate = productQuantity - 1;
      expandedCartEntries = createCartEntriesFromItemQuantity(quantityToCreate);
    }

    if (currentFavorite) {
      const cartEntries = handleTransformMenuObjectToCartItem({
        isNewItem: false,
        quantity: productQuantity,
      });

      return editFavorite({ cartEntries });
    }

    const currentCartEntry = handleTransformMenuObjectToCartItem({
      isNewItem: false,
      quantity: 1,
    });

    const finalItems = [...currentCartEntry, ...expandedCartEntries];

    if (finalItems.length) {
      const selections: IAddToCartSelectionAttributes = {
        itemModified: manualModifications.modifiers,
        comboSlotSelection: manualModifications.comboSlot,
        pickerAspectSelection: manualModifications.pickerAspects,
      };
      upsertCart(finalItems, selections);
    }

    const isEditingRewardItem = !!rewardBenefitId;
    const currentCartEntryHasRewardApplied =
      currentCartEntry?.[0] &&
      appliedLoyaltyRewardsArray?.some(reward => reward.cartId === currentCartEntry[0].cartId);

    // If current cart entry has a reward applied, apply the rewards to the expanded cart entries too
    let didApplyReward = false;
    if (currentCartEntryHasRewardApplied) {
      didApplyReward = handleRewardsRedemption(expandedCartEntries);
    }
    // If we are editing a reward item and none are currently applied, apply the rewards to the final items
    else if (isEditingRewardItem) {
      didApplyReward = handleRewardsRedemption(finalItems);
    }

    if (isOffer && selectedOffer && !cartIdEditing) {
      logOfferAddedToOrder({
        engineId: selectedOffer.loyaltyEngineId ?? '',
        sanityId: selectedOffer._id ?? '',
        name: selectedOffer.name?.localeRaw?.[0]?.children?.[0]?.text ?? '',
        redemptionMode: 'Online',
        distanceToStore,
        offerListPosition: toAmplitudeOfferListPosition(offerListPosition),
      });
    } else if (didApplyReward) {
      const rewardInfo = getAvailableRewardFromCartEntry(currentCartEntry[0] ?? finalItems[0]);
      logRewardAddedToOrder({
        engineId: rewardInfo?.id ?? '',
        sanityId: rewardInfo?.sanityId ?? '',
        name: rewardInfo?.name ?? '',
        redemptionMode: 'Online',
        distanceToStore,
      });
    }

    hapticImpact({ style: HapticsImpactStyle.Heavy });
    return editingCartEntry ? router.dismiss() : router.replace(routes.menu);
  }, [
    productQuantity,
    handleTransformMenuObjectToCartItem,
    currentFavorite,
    rewardBenefitId,
    cartIdEditing,
    appliedLoyaltyRewardsArray,
    editingCartEntry,
    router,
    createCartEntriesFromItemQuantity,
    editFavorite,
    manualModifications.modifiers,
    manualModifications.comboSlot,
    manualModifications.pickerAspects,
    upsertCart,
    handleRewardsRedemption,
    isOffer,
    selectedOffer,
    distanceToStore,
    offerListPosition,
    getAvailableRewardFromCartEntry,
  ]);

  return {
    addToOrder,
  };
};
